import React, {
  ChangeEvent,
  DragEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import {
  CloudUploadOutlined,
  DownloadOutlined,
  RefreshOutlined,
  CloseOutlined,
  InsertDriveFileOutlined,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";

import {
  GetASAISubDepartments,
  GetCurrentShiftImportRecords,
  GetDepartmentShifts,
  ImportShiftRecords,
} from "../../../services/asai";
import Breadcrumb from "../../../layouts/full-layout/breadcrumb/Breadcrumb";
import PageContainer from "../../../components/container/PageContainer";
import EightHoursViewShiftPage from "./8HourViewShiftPage";

// The roster view can contain a large table. Do not re-render it when only
// import-dialog/confirmation state changes. Its selection props are memoized below.
const MemoizedEightHoursViewShiftPage = React.memo(EightHoursViewShiftPage);

type EmployeeType = "payroll" | "contract" | "cnc";

interface ShiftConfig {
  key: string;
  label: string;
  shiftType?: string;
}

export interface SubDepartmentOption {
  id?: string | number;
  name: string;
  departmentName?: string;
}

/**
 * Adapter type for shift data returned by the department/sub-department API.
 * The callbacks are optional until the BE endpoints are wired in.
 */
export interface DepartmentShiftOption {
  id?: string | number;
  key?: string;
  shiftKey?: string;
  label: string;
  employeeType?: EmployeeType;
  shiftType?: string;
  subDepartmentId?: string | number;
  shiftHours?: number | string;
}

interface EightHoursImportShiftPageProps {
  /** Department ID belonging to the logged-in user. */
  loggedInDepartmentId?: string | number;
  /** Department name from the existing auth state. */
  loggedInDepartmentName?: string;
  /** Allowed sub-departments, if the parent already has them in auth/API state. */
  loggedInSubDepartments?: SubDepartmentOption[];
  /** Shifts already loaded for this department; may be scoped by subDepartmentId. */
  departmentShiftOptions?: DepartmentShiftOption[];
  /** Wire this to the existing department/sub-department API in the backend phase. */
  fetchSubDepartmentsByDepartmentId?: (
    departmentId: string | number,
  ) => Promise<SubDepartmentOption[]>;
  /** Wire this to the existing shifts-by-sub-department API in the backend phase. */
  fetchShiftsBySubDepartmentId?: (
    departmentId: string | number,
    subDepartmentId: string | number,
  ) => Promise<DepartmentShiftOption[]>;
}

interface ImportedShiftRow {
  personId: string;
  personName: string;
  offDay: string;
  month: string;
  contractor: string;
  subDepartment: string;
  subDepartmentId?: string;
  department: string;
  year: string;
  organization: string;
  shift: string;
  shiftKey: string;
  shiftMasterId?: number;
  employeeType: EmployeeType;
}

interface DraftImportRow extends ImportedShiftRow {
  rowId: string;
}

interface ShiftOption extends ShiftConfig {
  id?: string | number;
  employeeType: EmployeeType;
  value: string;
  dropdownLabel: string;
  shiftType: string;
  subDepartmentId?: string;
}

type EditableColumnKey =
  | "personId"
  | "personName"
  | "offDay"
  | "month"
  | "contractor"
  | "subDepartment"
  | "department"
  | "year"
  | "organization"
  | "employeeType";

const SHIFT_CONFIGS: Record<EmployeeType, ShiftConfig[]> = {
  payroll: [
    { key: "shift1", label: "Shift 1 (8:00 to 4:30)" },
    { key: "shift2", label: "Shift 2 (4:30 to 1:00)" },
    { key: "shift3", label: "Shift 3 (1:00 to 8:00)" },
  ],
  contract: [
    { key: "shift1", label: "Shift 1 (8:00 to 4:30)" },
    { key: "shift2", label: "Shift 2 (4:30 to 1:00)" },
    { key: "shift3", label: "Shift 3 (1:00 to 8:00)" },
  ],
  cnc: [
    { key: "shift1", label: "Shift 1 (8:00 to 4:30)" },
    { key: "shift2", label: "Shift 2 (4:30 to 1:00)" },
    { key: "shift3", label: "Shift 3 (1:00 to 8:00)" },
  ],
};

const EMPLOYEE_TYPE_LABELS: Record<EmployeeType, string> = {
  payroll: "Payroll",
  contract: "Contract",
  cnc: "Flex Apprentice",
};

const EMPLOYEE_TYPES: EmployeeType[] = ["payroll", "contract", "cnc"];

// Keep the confirmation dialog responsive for large Excel rosters by only
// rendering the rows around the visible scroll position.
const CONFIRMATION_ROW_HEIGHT = 48;
const CONFIRMATION_HEADER_HEIGHT = 56;
const CONFIRMATION_FILTER_HEIGHT = 48;
const CONFIRMATION_WINDOW_SIZE = 20;
const CONFIRMATION_OVERSCAN = 5;

// The pasted-data preview is virtualized too; large worksheets only mount a
// small number of table rows in the browser DOM at once.
const PASTE_PREVIEW_ROW_HEIGHT = 36;
const PASTE_PREVIEW_HEADER_HEIGHT = 42;
const PASTE_PREVIEW_WINDOW_SIZE = 28;
const PASTE_PREVIEW_OVERSCAN = 6;

const EMPLOYEE_TYPE_OPTIONS: Array<{ value: EmployeeType; label: string }> = [
  { value: "payroll", label: "Payroll" },
  { value: "contract", label: "Contract" },
  { value: "cnc", label: "Apprentice" },
];

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const TABLE_COLUMNS: Array<{ key: EditableColumnKey; label: string }> = [
  { key: "personId", label: "Person ID" },
  { key: "offDay", label: "Off Day" },
  { key: "month", label: "Month" },
  { key: "contractor", label: "Contractor" },
  { key: "subDepartment", label: "Sub Department" },
  { key: "year", label: "Year" },
  { key: "organization", label: "Organization" },
  { key: "employeeType", label: "Employee_Type" },
];

const normalize = (value: unknown) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const stringValue = (value: unknown) => String(value ?? "").trim();
const subDepartmentKey = (item: SubDepartmentOption) =>
  String(item.id ?? item.name);

function getField(item: any, ...keys: string[]): string {
  for (const key of keys) {
    if (item?.[key] !== undefined && item?.[key] !== null) {
      return stringValue(item[key]);
    }
  }
  return "";
}

function extractRecords(response: any): any[] {
  const result = response?.data?.data ?? response?.data ?? response ?? {};
  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.result)) return result.result;
  if (Array.isArray(result?.Result)) return result.Result;
  if (Array.isArray(result?.data)) return result.data;
  if (Array.isArray(result?.Data)) return result.Data;
  return [];
}

function monthNumber(value: unknown): number | null {
  const raw = stringValue(value);
  if (!raw) return null;
  const numeric = Number(raw);
  if (Number.isInteger(numeric) && numeric >= 1 && numeric <= 12)
    return numeric;
  const match = MONTH_NAMES.findIndex(
    (month) => normalize(month) === normalize(raw),
  );
  if (match >= 0) return match + 1;
  const shortMatch = MONTH_NAMES.findIndex(
    (month) => normalize(month.slice(0, 3)) === normalize(raw.slice(0, 3)),
  );
  return shortMatch >= 0 ? shortMatch + 1 : null;
}

function inferEmployeeType(item: DepartmentShiftOption): EmployeeType {
  if (item.employeeType) return item.employeeType;
  const label = normalize(item.label);
  if (label.includes("contract")) return "contract";
  if (label.includes("cnc") || label.includes("apprentice")) return "cnc";
  return "payroll";
}

function toShiftOptions(items: DepartmentShiftOption[]): ShiftOption[] {
  return items.map((item) => {
    const employeeType = inferEmployeeType(item);
    const key = stringValue(
      item.key ??
        item.shiftKey ??
        item.id ??
        normalize(item.label).replace(/[^a-z0-9]+/g, ""),
    );
    return {
      id: item.id,
      key,
      label: item.label,
      employeeType,
      value: `${employeeType}:${key}`,
      // Employee type is maintained in its own Excel column. Keep the
      // Shift dropdown value as the clean ShiftMaster name only.
      dropdownLabel: stringValue(item.label),
      shiftType: item.shiftType ?? item.label,
      subDepartmentId:
        item.subDepartmentId == null ? undefined : String(item.subDepartmentId),
    };
  });
}

// Keep every 8-hour shift selector in Shift Number order, independent of API order.
function compareShiftOptions(a: ShiftOption, b: ShiftOption): number {
  const getShiftOrder = (option: ShiftOption) => {
    const match = `${option.key} ${option.label}`.match(
      /\\bshift\\s*([a-z]+|\\d+)\\b/i,
    );
    const token = match?.[1] ?? "";
    const numeric = /^\\d+$/.test(token);
    return {
      numeric,
      value: numeric ? Number(token) : token.toUpperCase(),
    };
  };

  const left = getShiftOrder(a);
  const right = getShiftOrder(b);

  if (left.numeric && right.numeric) {
    return Number(left.value) - Number(right.value);
  }
  if (left.numeric !== right.numeric) {
    return left.numeric ? -1 : 1;
  }
  return String(left.value).localeCompare(String(right.value), undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

function normalizeApiRow(
  item: any,
  fallbackEmployeeType?: EmployeeType,
): ImportedShiftRow {
  const shift = getField(item, "shift", "Shift", "shiftName", "ShiftName");
  const organization = getField(
    item,
    "organization",
    "Organization",
    "org",
    "Org",
  );
  const typeHints = [
    getField(item, "employeeType", "EmployeeType"),
    getField(item, "employeeCategory", "EmployeeCategory"),
    organization,
    getField(item, "shiftType", "ShiftType"),
  ];
  const employeeType =
    typeHints.map((value) => parseEmployeeType(value)).find(Boolean) ??
    fallbackEmployeeType ??
    "payroll";
  const normalizedShift = SHIFT_CONFIGS[employeeType].find(
    (candidate) => normalize(candidate.label) === normalize(shift),
  );
  const now = new Date();
  const rawMonth = getField(
    item,
    "month",
    "Month",
    "monthName",
    "MonthName",
    "scheduleMonth",
    "ScheduleMonth",
  );
  const parsedMonth = monthNumber(rawMonth);
  const rawYear = getField(
    item,
    "year",
    "Year",
    "scheduleYear",
    "ScheduleYear",
  );
  const rawShiftNumber = getField(item, "shiftNumber", "ShiftNumber");
  const shiftToken = normalize(rawShiftNumber).replace(/[^a-z0-9]/g, "");
  const shiftKey = shiftToken
    ? shiftToken.startsWith("shift")
      ? shiftToken
      : `shift${shiftToken}`
    : normalizedShift?.key ?? getField(item, "shiftKey", "ShiftKey");

  return {
    personId: getField(item, "personId", "PersonId", "Person_ID", "person_ID"),
    personName: getField(
      item,
      "personName",
      "PersonName",
      "Person_Name",
      "name",
      "Name",
    ),
    offDay: getField(item, "offDay", "OffDay", "Off_day"),
    month:
      parsedMonth != null
        ? MONTH_NAMES[parsedMonth - 1]
        : rawMonth || MONTH_NAMES[now.getMonth()],
    contractor: getField(item, "contractor", "Contractor"),
    subDepartment: getField(
      item,
      "subDepartment",
      "SubDepartment",
      "subDepartmentName",
      "SubDepartmentName",
      "subDept",
      "SubDept",
      "sub_dept",
      "Sub_Dept",
    ),
    subDepartmentId:
      getField(
        item,
        "subDepartmentId",
        "SubDepartmentId",
        "subDeptId",
        "SubDeptId",
      ) || undefined,
    department: getField(
      item,
      "department",
      "Department",
      "departmentName",
      "DepartmentName",
      "dept",
      "Dept",
    ),
    year: rawYear || String(now.getFullYear()),
    organization,
    shift,
    shiftKey,
    shiftMasterId:
      Number(getField(item, "shiftMasterId", "ShiftMasterId")) || undefined,
    employeeType,
  };
}

type ImportColumnKey =
  | "personId"
  | "personName"
  | "offDay"
  | "month"
  | "contractor"
  | "subDepartment"
  | "year"
  | "organization"
  | "employeeType"
  | "shift";

const IMPORT_COLUMN_DEFINITIONS: Array<{
  key: ImportColumnKey;
  label: string;
  aliases: string[];
}> = [
  { key: "personId", label: "Person_ID", aliases: ["personid", "employeeid"] },
  { key: "offDay", label: "Off_day", aliases: ["offday", "weeklyoff"] },
  { key: "month", label: "Month", aliases: ["month"] },
  {
    key: "contractor",
    label: "Contractor",
    aliases: ["contractor", "contractorname"],
  },
  {
    key: "subDepartment",
    label: "Sub_Department",
    aliases: ["subdept", "subdepartment"],
  },
  { key: "year", label: "Year", aliases: ["year"] },
  {
    key: "organization",
    label: "Organization",
    aliases: ["organization", "organisation", "org"],
  },
  {
    key: "employeeType",
    label: "Employee_Type",
    aliases: ["employeetype", "type", "employee category", "employeecategory"],
  },
  {
    key: "shift",
    label: "Shift",
    aliases: ["shift", "shiftname", "shiftmastername"],
  },
];

type ConfirmationFilterKey = EditableColumnKey | "shift";
type ConfirmationFilters = Partial<Record<ConfirmationFilterKey, string>>;

interface ConfirmationFilterRowProps {
  appliedFilters: ConfirmationFilters;
  onApply: (filters: ConfirmationFilters) => void;
  disabled?: boolean;
}

const ConfirmationFilterRow = React.memo(function ConfirmationFilterRow({
  appliedFilters,
  onApply,
  disabled = false,
}: ConfirmationFilterRowProps) {
  const [draftFilters, setDraftFilters] =
    useState<ConfirmationFilters>(appliedFilters);

  useEffect(() => {
    setDraftFilters(appliedFilters);
  }, [appliedFilters]);

  // Debounce expensive full-draft filtering while keeping input typing local
  // to this memoized row instead of re-rendering every editable employee row.
  useEffect(() => {
    const timer = window.setTimeout(() => onApply(draftFilters), 250);
    return () => window.clearTimeout(timer);
  }, [draftFilters, onApply]);

  const setFilter = (key: ConfirmationFilterKey, value: string) => {
    setDraftFilters((previous) => ({ ...previous, [key]: value }));
  };

  return (
    <TableRow className="confirmation-filter-row">
      {TABLE_COLUMNS.map((column) => (
        <TableCell key={column.key}>
          <TextField
            size="small"
            fullWidth
            disabled={disabled}
            value={draftFilters[column.key] ?? ""}
            placeholder={`Filter ${column.label}`}
            onChange={(event) => setFilter(column.key, event.target.value)}
            inputProps={{ "aria-label": `Filter ${column.label}` }}
          />
        </TableCell>
      ))}
      <TableCell>
        <TextField
          size="small"
          fullWidth
          disabled={disabled}
          value={draftFilters.shift ?? ""}
          placeholder="Filter Shift"
          onChange={(event) => setFilter("shift", event.target.value)}
          inputProps={{ "aria-label": "Filter Shift" }}
        />
      </TableCell>
      <TableCell align="center">
        <Button
          size="small"
          disabled={
            disabled ||
            !Object.values(draftFilters).some((value) =>
              String(value ?? "").trim(),
            )
          }
          onClick={() => setDraftFilters({})}
          sx={{ minWidth: 54, textTransform: "none" }}
        >
          Clear
        </Button>
      </TableCell>
    </TableRow>
  );
});

// Prefill the Paste from Excel area with the exact supported template headers.
const PASTE_TEMPLATE_HEADER = IMPORT_COLUMN_DEFINITIONS.map(
  (column) => column.label,
).join("\t");

const normalizeHeader = (value: unknown) =>
  normalize(value).replace(/[^a-z0-9]/g, "");

function getColumnIndexes(headers: unknown[]): Record<ImportColumnKey, number> {
  const normalizedHeaders = headers.map(normalizeHeader);
  // Person_Name was part of older templates. Accept it for compatibility but
  // ignore it: the backend resolves the authoritative employee name by Person_ID.
  const knownAliases = new Set([
    ...IMPORT_COLUMN_DEFINITIONS.flatMap((definition) => definition.aliases),
    "personname",
    "employeename",
    "name",
  ]);
  const indexes = {} as Record<ImportColumnKey, number>;
  const missing: string[] = [];
  const duplicates: string[] = [];

  IMPORT_COLUMN_DEFINITIONS.forEach((definition) => {
    const matches = normalizedHeaders.reduce<number[]>(
      (found, header, index) => {
        if (definition.aliases.includes(header)) found.push(index);
        return found;
      },
      [],
    );

    if (matches.length === 0) {
      missing.push(definition.label);
    } else {
      indexes[definition.key] = matches[0];
      if (matches.length > 1) duplicates.push(definition.label);
    }
  });

  const unexpected = headers
    .map((header) => stringValue(header))
    .filter((header) => header && !knownAliases.has(normalizeHeader(header)));

  const problems: string[] = [];
  if (missing.length) problems.push(`Missing columns: ${missing.join(", ")}.`);
  if (duplicates.length)
    problems.push(`Duplicate columns: ${duplicates.join(", ")}.`);
  if (unexpected.length) {
    const uniqueUnexpected = unexpected.filter(
      (column, index) => unexpected.indexOf(column) === index,
    );

    problems.push(`Unexpected columns: ${uniqueUnexpected.join(", ")}.`);
  }

  if (problems.length) {
    throw new Error(
      `Invalid Excel template format. ${problems.join(
        " ",
      )} Use the Download Sample template and keep all 9 required column headers. Person_Name is optional in old files and is ignored; the backend resolves names from Person_ID. Column order may be changed, but header names must remain valid.`,
    );
  }

  return indexes;
}

function parseEmployeeType(value: unknown): EmployeeType | null {
  const normalized = normalize(value);
  if (normalized === "payroll" || normalized.includes("payroll"))
    return "payroll";
  if (normalized === "contract" || normalized.includes("contract"))
    return "contract";
  if (normalized === "cnc" || normalized.includes("apprentice")) return "cnc";
  return null;
}

function formatEmployeeTypeLabel(value: EmployeeType): string {
  return EMPLOYEE_TYPE_LABELS[value] === "CNC Apprentice"
    ? "Apprentice"
    : EMPLOYEE_TYPE_LABELS[value];
}

function matchingHeaderCount(row: unknown[]): number {
  const cells = row.map(normalizeHeader).filter(Boolean);
  return IMPORT_COLUMN_DEFINITIONS.filter((definition) =>
    cells.some((cell) => definition.aliases.includes(cell)),
  ).length;
}

function hasHeaderRow(row: unknown[]): boolean {
  // Require several known header cells, avoiding false positives when a data
  // cell happens to contain a word such as "Contractor".
  return matchingHeaderCount(row) >= 3;
}

function cleanMatrix(matrix: unknown[][]): unknown[][] {
  // Ignore fully empty rows (including rows containing only spaces/tabs).
  const rows = matrix
    .filter(
      (row) =>
        Array.isArray(row) && row.some((cell) => stringValue(cell) !== ""),
    )
    .map((row) => [...row]);

  if (!rows.length) return [];

  const columnCount = rows.reduce((max, row) => Math.max(max, row.length), 0);
  const columnHasValue = (columnIndex: number) =>
    rows.some((row) => stringValue(row[columnIndex]) !== "");
  const firstRowHasHeaders = hasHeaderRow(rows[0] ?? []);
  let keptColumns: number[];

  if (firstRowHasHeaders) {
    // Header-based paste can safely discard any column that is empty across
    // the whole copied range, including accidentally selected empty columns.
    keptColumns = Array.from(
      { length: columnCount },
      (_, index) => index,
    ).filter(columnHasValue);
  } else {
    // Data-only paste has no headers to re-map its columns. Trim empty columns
    // only from the left/right edges so intentionally blank middle fields keep
    // their template positions (e.g. an empty Contractor column).
    let firstColumn = 0;
    let lastColumn = columnCount - 1;
    while (firstColumn <= lastColumn && !columnHasValue(firstColumn)) {
      firstColumn += 1;
    }
    while (lastColumn >= firstColumn && !columnHasValue(lastColumn)) {
      lastColumn -= 1;
    }
    keptColumns = Array.from(
      { length: Math.max(0, lastColumn - firstColumn + 1) },
      (_, index) => firstColumn + index,
    );
  }

  return rows.map((row) =>
    keptColumns.map((columnIndex) => row[columnIndex] ?? ""),
  );
}

function parsePastedMatrix(text: string): unknown[][] {
  if (!text || !text.trim()) return [];

  // Preserve leading/trailing tabs so an empty copied edge column does not
  // shift every value left/right. Remove CR characters but do not trim the
  // entire string, because String.trim() would also remove tabs at its edges.
  const normalizedText = text.replace(/\r\n?/g, "\n");
  const delimiter = normalizedText.includes("\t") ? "\t" : ",";
  const rawRows = normalizedText
    .split("\n")
    .map((line) => line.split(delimiter));
  const parsedRows = cleanMatrix(rawRows);

  if (!parsedRows.length) return [];

  // If the user replaces the prefilled header with only data rows, assume the
  // documented template column order and add the header automatically.
  const rowsWithHeader = hasHeaderRow(parsedRows[0])
    ? parsedRows
    : [IMPORT_COLUMN_DEFINITIONS.map((column) => column.label), ...parsedRows];

  // A default header may remain above the copied range, or users may paste the
  // header more than once. Keep the first header and discard later header rows.
  return rowsWithHeader.filter(
    (row, index) => index === 0 || !hasHeaderRow(row),
  );
}

function getPastePreviewColumnWidth(header: unknown): number {
  switch (normalizeHeader(header)) {
    case "personid":
      return 105;
    case "personname":
      return 220;
    case "offday":
      return 90;
    case "month":
      return 105;
    case "contractor":
      return 135;
    case "subdepartment":
      return 165;
    case "year":
      return 85;
    case "organization":
      return 145;
    case "employeetype":
      return 130;
    case "shift":
    case "shiftname":
    case "shiftmastername":
      return 260;
    default:
      return 140;
  }
}

export default function EightHoursImportShiftPage({
  loggedInDepartmentId,
  loggedInDepartmentName = "",
  loggedInSubDepartments,
}: EightHoursImportShiftPageProps) {
  const importDialogFileRef = useRef<HTMLInputElement | null>(null);
  // Hidden native textarea captures real Excel clipboard pastes; the visible
  // paste area is rendered as a spreadsheet-style grid, never raw TSV text.
  const pasteCaptureRef = useRef<HTMLTextAreaElement | null>(null);
  const now = new Date();

  const [userSubDepartments, setUserSubDepartments] = useState<
    SubDepartmentOption[]
  >(loggedInSubDepartments ?? []);
  const [employeeType, setEmployeeType] = useState<EmployeeType>("payroll");
  const [selectedShiftValue, setSelectedShiftValue] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(
    String(now.getMonth() + 1),
  );
  const [selectedYear, setSelectedYear] = useState(String(now.getFullYear()));

  const [shiftOptionsBySubDepartment, setShiftOptionsBySubDepartment] =
    useState<Record<string, ShiftOption[]>>({});
  const [loadingScope, setLoadingScope] = useState(false);
  const [scopeError, setScopeError] = useState("");

  const [serverRows, setServerRows] = useState<ImportedShiftRow[]>([]);
  const [localImportedRows, setLocalImportedRows] = useState<
    ImportedShiftRow[]
  >([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [searchText, setSearchText] = useState("");
  const [columnFilters, setColumnFilters] = useState<
    Partial<Record<keyof ImportedShiftRow, string>>
  >({});
  const [showAllFilters, setShowAllFilters] = useState(false);
  const [orderBy, setOrderBy] = useState<keyof ImportedShiftRow>("personId");
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const confirmTableContainerRef = useRef<HTMLDivElement | null>(null);
  const [confirmWindowStart, setConfirmWindowStart] = useState(0);
  const confirmWindowStartRef = useRef(0);
  const confirmScrollTopRef = useRef(0);
  const confirmScrollRafRef = useRef<number | null>(null);
  const [confirmationFilters, setConfirmationFilters] =
    useState<ConfirmationFilters>({});
  const pastePreviewContainerRef = useRef<HTMLDivElement | null>(null);
  const [pastePreviewWindowStart, setPastePreviewWindowStart] = useState(0);
  const [inputMode, setInputMode] = useState<"file" | "paste">("file");
  const [selectedFileName, setSelectedFileName] = useState("");
  const [pasteText, setPasteText] = useState(PASTE_TEMPLATE_HEADER);
  // Parse once per pasteText change; do not re-split the whole clipboard text
  // on every render (the old disabled predicate did that repeatedly).
  const pastePreviewMatrix = useMemo(
    () => parsePastedMatrix(pasteText),
    [pasteText],
  );
  const pastePreviewHeaders =
    pastePreviewMatrix[0] ??
    IMPORT_COLUMN_DEFINITIONS.map((column) => column.label);
  // Copy the rows only when the parsed matrix changes; do not slice large sheets
  // again when opening/closing dialogs or editing unrelated state.
  const pastePreviewRows = useMemo(
    () => pastePreviewMatrix.slice(1),
    [pastePreviewMatrix],
  );
  const pastePreviewRowCount = pastePreviewRows.length;
  const pastePreviewStartIndex = Math.min(
    pastePreviewWindowStart,
    Math.max(0, pastePreviewRows.length - PASTE_PREVIEW_WINDOW_SIZE),
  );
  const pastePreviewEndIndex = Math.min(
    pastePreviewRows.length,
    pastePreviewStartIndex + PASTE_PREVIEW_WINDOW_SIZE,
  );
  const pastePreviewVisibleRows = pastePreviewRows.slice(
    pastePreviewStartIndex,
    pastePreviewEndIndex,
  );
  const pastePreviewTopSpacerHeight =
    pastePreviewStartIndex * PASTE_PREVIEW_ROW_HEIGHT;
  const pastePreviewBottomSpacerHeight =
    (pastePreviewRows.length - pastePreviewEndIndex) * PASTE_PREVIEW_ROW_HEIGHT;
  const [draftRows, setDraftRows] = useState<DraftImportRow[]>([]);
  const filteredDraftRows = useMemo(() => {
    const activeFilters = Object.entries(confirmationFilters).filter(
      ([, value]) => String(value ?? "").trim() !== "",
    ) as Array<[ConfirmationFilterKey, string]>;
    if (activeFilters.length === 0) return draftRows;

    const normalizedFilters = activeFilters.map(
      ([key, value]) => [key, normalize(value)] as const,
    );

    return draftRows.filter((row) =>
      normalizedFilters.every(([key, value]) => {
        const fieldValue =
          key === "shift" ? row.shift : row[key as keyof DraftImportRow];
        const displayValue =
          key === "employeeType"
            ? formatEmployeeTypeLabel(row.employeeType)
            : fieldValue;
        return normalize(displayValue).includes(value);
      }),
    );
  }, [draftRows, confirmationFilters]);

  // Virtualized view: all matching employees remain reachable, while only a
  // small number of editable rows are mounted in the DOM.
  const confirmationStartIndex = Math.min(
    confirmWindowStart,
    Math.max(0, filteredDraftRows.length - CONFIRMATION_WINDOW_SIZE),
  );
  const confirmationEndIndex = Math.min(
    filteredDraftRows.length,
    confirmationStartIndex + CONFIRMATION_WINDOW_SIZE,
  );
  const draftRowsForConfirmation = filteredDraftRows.slice(
    confirmationStartIndex,
    confirmationEndIndex,
  );
  const confirmationTopSpacerHeight =
    confirmationStartIndex * CONFIRMATION_ROW_HEIGHT;
  const confirmationBottomSpacerHeight =
    (filteredDraftRows.length - confirmationEndIndex) * CONFIRMATION_ROW_HEIGHT;
  const [dragging, setDragging] = useState(false);
  const [importing, setImporting] = useState(false);
  const [rosterRefreshKey, setRosterRefreshKey] = useState(0);

  useEffect(() => {
    setPastePreviewWindowStart(0);
    pastePreviewContainerRef.current?.scrollTo({ top: 0, left: 0 });
  }, [pastePreviewMatrix]);

  useEffect(() => {
    let cancelled = false;

    const loadSubDepartments = async () => {
      setLoadingScope(true);
      setScopeError("");

      try {
        const response = await GetASAISubDepartments();

        const records = extractRecords(response);

        const options: SubDepartmentOption[] = records.map((item: any) => ({
          id: item.id ?? item.Id,
          name: item.machineName ?? item.MachineName,
        }));

        if (!cancelled) {
          setUserSubDepartments(options);
          setSelectedShiftValue("");
        }
      } catch (error) {
        console.error("Failed to load sub-departments:", error);

        if (!cancelled) {
          setUserSubDepartments([]);
          setScopeError(
            "Unable to load sub-departments. Please refresh and try again.",
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingScope(false);
        }
      }
    };

    void loadSubDepartments();

    return () => {
      cancelled = true;
    };
  }, []);

  const subDepartmentOptions = useMemo(
    () =>
      userSubDepartments.map((item) => ({
        ...item,
        optionKey: subDepartmentKey(item),
      })),
    [userSubDepartments],
  );

  // Populate scoped shift lists. If the BE loader is not wired yet, use the current
  // FE shift configuration so the upload/review workflow can be developed independently.
  useEffect(() => {
    let cancelled = false;

    const loadShiftScope = async () => {
      setLoadingScope(true);
      setScopeError("");

      try {
        // Department ID is resolved from the backend session.
        const response = await GetDepartmentShifts(8);
        const records = extractRecords(response);

        const options: DepartmentShiftOption[] = records
          .map((item: any): DepartmentShiftOption => {
            const rawNumber = getField(item, "shiftNumber", "ShiftNumber");

            const normalizedNumber = normalize(rawNumber).replace(
              /[^a-z0-9]/g,
              "",
            );

            // Examples: 1 -> shift1, A -> shifta.
            const key = normalizedNumber.startsWith("shift")
              ? normalizedNumber
              : `shift${normalizedNumber}`;

            const shiftHours = getField(item, "shiftHours", "ShiftHours");
            const shiftType = normalize(
              getField(item, "shiftType", "ShiftType"),
            );

            const employeeType: EmployeeType = shiftType.includes("contract")
              ? "contract"
              : shiftType.includes("apprentice")
              ? "cnc"
              : "payroll";

            return {
              id: item.id ?? item.Id,
              key,
              shiftKey: key,
              label: getField(item, "shiftName", "ShiftName"),
              employeeType,
              shiftType: getField(item, "shiftType", "ShiftType"),
              shiftHours,
            };
          })
          .filter(
            (item) =>
              item.key &&
              item.label &&
              (item.shiftHours == null || Number(item.shiftHours) === 8),
          );

        // ShiftMaster belongs to the logged-in department. Excel rows can
        // contain employees from any valid sub-department in that department.
        if (!cancelled) {
          setShiftOptionsBySubDepartment({
            __all__: toShiftOptions(options).sort(compareShiftOptions),
          });

          if (options.length === 0) {
            setScopeError(
              "No active 8-hour shifts are configured for your department.",
            );
          }
        }
      } catch (error) {
        console.error("Failed to load department shifts:", error);

        if (!cancelled) {
          setShiftOptionsBySubDepartment({});
          setScopeError(
            "Unable to load shifts for your department. Please refresh and try again.",
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingScope(false);
        }
      }
    };

    void loadShiftScope();

    return () => {
      cancelled = true;
    };
  }, []);

  const allSelectedShiftOptions = shiftOptionsBySubDepartment.__all__ ?? [];
  const selectedShiftOptions = useMemo(
    () =>
      allSelectedShiftOptions.filter(
        (item) => item.employeeType === employeeType,
      ),
    [allSelectedShiftOptions, employeeType],
  );
  const selectedShift = selectedShiftOptions.find(
    (item) => item.value === selectedShiftValue,
  );
  const employeeTypeLabel = EMPLOYEE_TYPE_LABELS[employeeType];
  const viewShiftSelection = useMemo(
    () => ({
      // The embedded roster displays every shift in separate tabs.
      // Only the schedule period is shared with the Import page.
      month: Number(selectedMonth),
      year: Number(selectedYear),
    }),
    [selectedMonth, selectedYear],
  );

  useEffect(() => {
    if (!selectedShiftOptions.length) {
      if (selectedShiftValue) setSelectedShiftValue("");
      return;
    }
    if (
      !selectedShiftOptions.some((item) => item.value === selectedShiftValue)
    ) {
      setSelectedShiftValue(selectedShiftOptions[0].value);
    }
  }, [selectedShiftOptions, selectedShiftValue]);

  const loadShiftRows = useCallback(async () => {
    setLoadingRows(true);
    setLoadError("");
    try {
      const response = await GetCurrentShiftImportRecords({
        scheduleMonth: Number(selectedMonth),
        scheduleYear: Number(selectedYear),
        shiftHours: 8,
      });
      const rows = extractRecords(response).map((row) => normalizeApiRow(row));
      setServerRows(rows);
    } catch (error) {
      console.error("Failed to load 8-hour ASAI shift import records:", error);
      setServerRows([]);
      setLoadError(
        "Unable to load 8-hour employee records from ASAI ShiftImportRecords. Please refresh and try again.",
      );
    } finally {
      setLoadingRows(false);
    }
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    void loadShiftRows();
  }, [loadShiftRows]);

  const allRows = useMemo(() => {
    const combined = new Map<string, ImportedShiftRow>();
    [...serverRows, ...localImportedRows].forEach((row) => {
      const key = [
        normalize(row.employeeType),
        normalize(row.shiftKey || row.shift),
        normalize(row.personId),
        normalize(row.month),
        normalize(row.year),
        normalize(row.subDepartment),
      ].join("|");
      const previous = combined.get(key);
      const merged = { ...previous, ...row };
      if (!row.personName?.trim() && previous?.personName?.trim()) {
        merged.personName = previous.personName;
      }
      combined.set(key, merged);
    });
    return Array.from(combined.values());
  }, [serverRows, localImportedRows]);

  const filteredRows = useMemo(() => {
    const selectedMonthNumber = Number(selectedMonth);
    const search = normalize(searchText);
    const departmentKey = normalize(loggedInDepartmentName);
    return allRows.filter((row) => {
      if (
        departmentKey &&
        row.department &&
        normalize(row.department) !== departmentKey
      )
        return false;
      if (monthNumber(row.month) !== selectedMonthNumber) return false;
      if (String(row.year || "") !== selectedYear) return false;
      if (selectedShift) {
        const sameType = row.employeeType === selectedShift.employeeType;
        const sameShift =
          normalize(row.shiftKey) === normalize(selectedShift.key) ||
          normalize(row.shift) === normalize(selectedShift.label);
        if (!sameType || !sameShift) return false;
      }
      if (search) {
        const searchable = [
          row.personId,
          row.personName,
          row.offDay,
          row.month,
          row.contractor,
          row.subDepartment,
          row.department,
          row.year,
          row.organization,
          row.shift,
        ]
          .map(normalize)
          .join(" ");
        if (!searchable.includes(search)) return false;
      }
      const columnMatch = (
        Object.entries(columnFilters) as Array<[string, string | undefined]>
      ).every(([field, value]) => {
        if (!value?.trim()) return true;
        const fieldValue =
          field === "shift" ? row.shift : row[field as keyof ImportedShiftRow];
        return normalize(fieldValue).includes(normalize(value));
      });
      return columnMatch;
    });
  }, [
    allRows,
    selectedMonth,
    selectedYear,
    searchText,
    loggedInDepartmentName,
    selectedShift,
    columnFilters,
  ]);

  const tableColumns = useMemo(
    () => [
      ...TABLE_COLUMNS,
      { key: "shift" as keyof ImportedShiftRow, label: "Shift" },
    ],
    [],
  );

  const sortedRows = useMemo(() => {
    return [...filteredRows].sort((a, b) => {
      const aValue = normalize(a[orderBy]);
      const bValue = normalize(b[orderBy]);
      const comparison = aValue.localeCompare(bValue, undefined, {
        numeric: true,
        sensitivity: "base",
      });
      return order === "asc" ? comparison : -comparison;
    });
  }, [filteredRows, orderBy, order]);

  const pagedRows = useMemo(
    () =>
      sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedRows, page, rowsPerPage],
  );

  useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(filteredRows.length / rowsPerPage) - 1,
    );
    if (page > maxPage) setPage(maxPage);
  }, [filteredRows.length, page, rowsPerPage]);

  const handleTableSort = (field: keyof ImportedShiftRow) => {
    const nextOrder = orderBy === field && order === "asc" ? "desc" : "asc";
    setOrder(nextOrder);
    setOrderBy(field);
    setPage(0);
  };

  const resetTableFilters = () => {
    setSearchText("");
    setColumnFilters({});
    setShowAllFilters(false);
    setPage(0);
  };

  const makeDraftRows = useCallback(
    (matrix: unknown[][]): DraftImportRow[] => {
      const rows = cleanMatrix(matrix);
      if (!rows.length) {
        throw new Error(
          "The selected file is empty. Upload a completed Shift Data sheet from Download Sample.",
        );
      }

      const firstRowIsHeader = hasHeaderRow(rows[0] ?? []);
      if (!firstRowIsHeader) {
        throw new Error(
          "Invalid Excel format. Required headers: Person_ID, Off_day, Month, Contractor, Sub_Department, Year, Organization, Employee_Type and Shift. Person_Name is optional in older files and is ignored; names are resolved by Person_ID.",
        );
      }

      const headers = [...rows[0]];
      while (
        headers.length > 0 &&
        stringValue(headers[headers.length - 1]) === ""
      )
        headers.pop();
      const dataRows = rows.slice(1);
      if (!dataRows.length)
        throw new Error("The sheet contains headers but no employee rows.");

      const indexes = getColumnIndexes(headers);
      const seenPersonIds = new Map<string, number>();

      // Build lookup maps once. The old implementation concatenated and scanned
      // all existing roster rows for every imported employee (O(imports x roster)).
      const knownEmployeesById = new Map<string, ImportedShiftRow>();
      for (const employee of localImportedRows) {
        const key = normalize(employee.personId);
        if (key && !knownEmployeesById.has(key)) {
          knownEmployeesById.set(key, employee);
        }
      }
      for (const employee of serverRows) {
        const key = normalize(employee.personId);
        if (key && !knownEmployeesById.has(key)) {
          knownEmployeesById.set(key, employee);
        }
      }

      const subDepartmentsByName = new Map<string, SubDepartmentOption>();
      for (const option of subDepartmentOptions) {
        const key = normalize(option.name);
        if (key && !subDepartmentsByName.has(key)) {
          subDepartmentsByName.set(key, option);
        }
      }

      const shiftIdentity = (value: unknown): string => {
        const normalized = normalize(value);
        const match = normalized.match(/\bshift\s*([a-z0-9]+)\b/i);
        return match
          ? `shift${match[1]}`
          : normalized.replace(/[^a-z0-9]/g, "");
      };
      const shiftsByEmployeeType = new Map<EmployeeType, ShiftOption[]>();
      for (const option of allSelectedShiftOptions) {
        const current = shiftsByEmployeeType.get(option.employeeType) ?? [];
        current.push(option);
        shiftsByEmployeeType.set(option.employeeType, current);
      }

      const mapped = dataRows.map((row, index): DraftImportRow => {
        const rowNumber = index + 2;
        const getCell = (key: ImportColumnKey) =>
          stringValue(row[indexes[key]] ?? "");

        const blankHeaderWithData = headers.findIndex(
          (header, columnIndex) =>
            stringValue(header) === "" && stringValue(row[columnIndex]) !== "",
        );
        if (blankHeaderWithData >= 0) {
          throw new Error(
            `Row ${rowNumber}: data exists in column ${
              blankHeaderWithData + 1
            }, which has no header.`,
          );
        }
        if (
          row.slice(headers.length).some((cell) => stringValue(cell) !== "")
        ) {
          throw new Error(
            `Row ${rowNumber}: data exists beyond the last template column.`,
          );
        }

        const personId = getCell("personId");
        const offDay = getCell("offDay");
        const knownEmployee = knownEmployeesById.get(normalize(personId));
        // Use cached roster names for review when available. The API/SQL import
        // resolves the authoritative name from Person_ID and never requires Excel Name.
        const personName = knownEmployee?.personName?.trim() ?? "";
        const missingFields = [
          !personId && "Person_ID",
          !offDay && "Off_day",
        ].filter(Boolean);
        if (missingFields.length) {
          throw new Error(
            `Row ${rowNumber}: ${missingFields.join(", ")} ${
              missingFields.length === 1 ? "is" : "are"
            } required.`,
          );
        }

        const personIdKey = normalize(personId);
        const previousRow = seenPersonIds.get(personIdKey);
        if (previousRow !== undefined) {
          throw new Error(
            `Row ${rowNumber}: Person_ID "${personId}" is duplicated; it already appears in row ${previousRow}.`,
          );
        }
        seenPersonIds.set(personIdKey, rowNumber);

        const sheetMonth = getCell("month");
        const parsedMonth = monthNumber(sheetMonth);
        if (parsedMonth === null) {
          throw new Error(
            `Row ${rowNumber}: Month is required. Enter a month name (e.g. October) or 1–12 in Excel.`,
          );
        }

        const sheetYear = getCell("year");
        const parsedYear = Number(sheetYear);
        if (
          !/^\d{4}$/.test(sheetYear) ||
          !Number.isInteger(parsedYear) ||
          parsedYear < 2000 ||
          parsedYear > 2100
        ) {
          throw new Error(
            `Row ${rowNumber}: Year is invalid. Enter a four-digit year between 2000 and 2100.`,
          );
        }

        const sheetSubDepartment = getCell("subDepartment");
        if (!sheetSubDepartment)
          throw new Error(`Row ${rowNumber}: Sub_Department is required.`);
        const matchedSubDepartment = subDepartmentsByName.get(
          normalize(sheetSubDepartment),
        );
        if (!matchedSubDepartment) {
          throw new Error(
            `Row ${rowNumber}: Sub_Department "${sheetSubDepartment}" is not valid for this department. Select a value from the Excel dropdown.`,
          );
        }

        const employeeTypeCell = getCell("employeeType");
        const parsedEmployeeType = parseEmployeeType(employeeTypeCell);
        if (!parsedEmployeeType) {
          throw new Error(
            `Row ${rowNumber}: Employee_Type must be Payroll, Contract, or Apprentice. Select a value from the Excel dropdown.`,
          );
        }

        const shiftCell = getCell("shift");
        if (!shiftCell)
          throw new Error(
            `Row ${rowNumber}: Shift is required. Select a shift from the Excel dropdown.`,
          );
        // First match the full shift label. For older workbooks where the
        // label omits AM/PM (for example "Shift 2 (6:30 to 5:00)"), also
        // match its Shift number/letter. Employee type is always matched
        // separately so Shift 1 for Contract cannot resolve to Payroll.
        const enteredShiftIdentity = shiftIdentity(shiftCell);
        const employeeTypeShifts =
          shiftsByEmployeeType.get(parsedEmployeeType) ?? [];
        const normalizedShiftCell = normalize(shiftCell);
        const matchingShifts = employeeTypeShifts.filter(
          (option) =>
            normalize(option.dropdownLabel) === normalizedShiftCell ||
            normalize(option.label) === normalizedShiftCell ||
            (enteredShiftIdentity !== "" &&
              shiftIdentity(option.label) === enteredShiftIdentity),
        );
        if (matchingShifts.length === 0) {
          throw new Error(
            `Row ${rowNumber}: Shift "${shiftCell}" is not configured for ${formatEmployeeTypeLabel(
              parsedEmployeeType,
            )} in your department. Choose a shift that matches Employee_Type.`,
          );
        }
        if (matchingShifts.length > 1) {
          throw new Error(
            `Row ${rowNumber}: Shift "${shiftCell}" is ambiguous for ${formatEmployeeTypeLabel(
              parsedEmployeeType,
            )}. Please download the latest sample template and select a shift that uniquely matches this employee type.`,
          );
        }
        const resolvedShift = matchingShifts[0];
        const shiftMasterId = Number(resolvedShift.id);
        if (!Number.isInteger(shiftMasterId) || shiftMasterId <= 0) {
          throw new Error(
            `Row ${rowNumber}: Shift_Master_ID could not be resolved. Refresh the page and download the latest sample.`,
          );
        }

        return {
          rowId: `${Date.now()}-${index}-${Math.random()
            .toString(36)
            .slice(2, 8)}`,
          personId,
          personName,
          offDay: offDay.toUpperCase(),
          month: MONTH_NAMES[parsedMonth - 1],
          contractor: getCell("contractor"),
          subDepartment: matchedSubDepartment.name,
          subDepartmentId:
            matchedSubDepartment.id == null
              ? undefined
              : String(matchedSubDepartment.id),
          department: loggedInDepartmentName,
          year: String(parsedYear),
          organization: getCell("organization"),
          shift: resolvedShift.label,
          shiftKey: resolvedShift.key,
          shiftMasterId,
          employeeType: parsedEmployeeType,
        };
      });

      const periods = new Set(
        mapped.map((row) => `${monthNumber(row.month)}-${row.year}`),
      );
      if (periods.size !== 1) {
        throw new Error(
          "All rows in one roster import must have the same Month and Year. You can mix shifts and employee types in the same file, but use one schedule period per workbook.",
        );
      }
      if (!mapped.length)
        throw new Error(
          "No employee records found in the uploaded or pasted sheet.",
        );
      return mapped;
    },
    [
      allSelectedShiftOptions,
      subDepartmentOptions,
      loggedInDepartmentName,
      localImportedRows,
      serverRows,
    ],
  );

  const openImportDialog = () => {
    if (!allSelectedShiftOptions.length) {
      toast.error(
        "No active shifts are available for your department. Refresh and try again.",
      );
      return;
    }
    setSelectedFileName("");
    setPasteText(PASTE_TEMPLATE_HEADER);
    setDraftRows([]);
    setInputMode("file");
    setImportDialogOpen(true);
  };

  const processFile = async (file: File) => {
    const extension = file.name.split(".").pop()?.toLowerCase();
    if (extension !== "xlsx" && extension !== "xls" && extension !== "csv") {
      toast.error("Please upload an Excel or CSV file (.xlsx, .xls, .csv).");
      return;
    }
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array", raw: false });
      const sheetName = workbook.SheetNames[0];
      if (!sheetName)
        throw new Error("Excel file does not contain a worksheet.");
      const worksheet = workbook.Sheets[sheetName];
      if (!worksheet) throw new Error("Unable to read the worksheet.");
      const matrix = XLSX.utils.sheet_to_json<unknown[]>(worksheet, {
        header: 1,
        defval: "",
        raw: false,
      });
      const parsedRows = makeDraftRows(matrix);
      setSelectedFileName(file.name);
      setDraftRows(parsedRows);
      toast.success(
        `${parsedRows.length} employee records are ready to review.`,
      );
    } catch (error: any) {
      setSelectedFileName("");
      setDraftRows([]);
      toast.error(error?.message || "Unable to read the selected Excel file.");
    }
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    event.target.value = "";
    if (file) await processFile(file);
  };

  const handleDrop = async (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) await processFile(file);
  };

  const preparePastedRows = () => {
    try {
      const parsedRows = makeDraftRows(pastePreviewMatrix);
      setSelectedFileName("Pasted Excel data");
      setDraftRows(parsedRows);
      toast.success(
        `${parsedRows.length} pasted employee records are ready to review.`,
      );
    } catch (error: any) {
      setDraftRows([]);
      toast.error(error?.message || "Unable to read the pasted data.");
    }
  };

  const openConfirmation = () => {
    if (!draftRows.length) {
      toast.error("Choose an Excel file or prepare pasted data first.");
      return;
    }
    confirmWindowStartRef.current = 0;
    setConfirmWindowStart(0);
    setConfirmationFilters({});
    setImportDialogOpen(false);
    setConfirmDialogOpen(true);
  };

  const applyConfirmationFilters = useCallback(
    (filters: ConfirmationFilters) => {
      setConfirmationFilters(filters);
      confirmWindowStartRef.current = 0;
      confirmScrollTopRef.current = 0;
      setConfirmWindowStart(0);
      confirmTableContainerRef.current?.scrollTo({ top: 0, left: 0 });
    },
    [],
  );

  const handleConfirmationTableScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      confirmScrollTopRef.current = event.currentTarget.scrollTop;
      if (confirmScrollRafRef.current !== null) return;

      confirmScrollRafRef.current = window.requestAnimationFrame(() => {
        confirmScrollRafRef.current = null;
        const firstVisibleRow = Math.max(
          0,
          Math.floor(
            Math.max(
              0,
              confirmScrollTopRef.current -
                CONFIRMATION_HEADER_HEIGHT -
                CONFIRMATION_FILTER_HEIGHT,
            ) / CONFIRMATION_ROW_HEIGHT,
          ),
        );
        const maxStart = Math.max(
          0,
          filteredDraftRows.length - CONFIRMATION_WINDOW_SIZE,
        );
        const desiredStart = Math.max(
          0,
          firstVisibleRow - CONFIRMATION_OVERSCAN,
        );
        // Update the mounted row window in batches to reduce scroll-time renders.
        const chunkedStart = Math.floor(desiredStart / 6) * 6;
        const nextStart =
          desiredStart >= maxStart
            ? maxStart
            : Math.min(maxStart, chunkedStart);

        if (confirmWindowStartRef.current !== nextStart) {
          confirmWindowStartRef.current = nextStart;
          setConfirmWindowStart(nextStart);
        }
      });
    },
    [filteredDraftRows.length],
  );

  useEffect(() => {
    if (!confirmDialogOpen) return;
    confirmWindowStartRef.current = 0;
    confirmScrollTopRef.current = 0;
    setConfirmWindowStart(0);
    const frame = window.requestAnimationFrame(() => {
      confirmTableContainerRef.current?.scrollTo({ top: 0, left: 0 });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [confirmDialogOpen, confirmationFilters]);

  useEffect(
    () => () => {
      if (confirmScrollRafRef.current !== null) {
        window.cancelAnimationFrame(confirmScrollRafRef.current);
      }
    },
    [],
  );

  const handlePastePreviewScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const firstVisibleRow = Math.max(
      0,
      Math.floor(
        Math.max(
          0,
          event.currentTarget.scrollTop - PASTE_PREVIEW_HEADER_HEIGHT,
        ) / PASTE_PREVIEW_ROW_HEIGHT,
      ),
    );
    const maxStart = Math.max(
      0,
      pastePreviewRows.length - PASTE_PREVIEW_WINDOW_SIZE,
    );
    const nextStart = Math.min(
      maxStart,
      Math.max(0, firstVisibleRow - PASTE_PREVIEW_OVERSCAN),
    );
    setPastePreviewWindowStart((previous) =>
      previous === nextStart ? previous : nextStart,
    );
  };

  const updateDraftCell = (
    rowId: string,
    field: Exclude<EditableColumnKey, "employeeType" | "personName">,
    value: string,
  ) => {
    setDraftRows((previous) =>
      previous.map((row) =>
        row.rowId === rowId ? { ...row, [field]: value } : row,
      ),
    );
  };

  const handleConfirmImport = async () => {
    const invalidRow = draftRows.find(
      (row) =>
        !row.personId.trim() ||
        !row.offDay.trim() ||
        !row.subDepartment.trim() ||
        !row.shiftMasterId ||
        !row.shiftKey ||
        !row.employeeType,
    );
    if (invalidRow) {
      toast.error(
        `Complete Person_ID, Off_day, Sub_Department, Employee_Type and Shift for ${
          invalidRow.personName || invalidRow.personId || "the highlighted row"
        }.`,
      );
      return;
    }

    const scheduleMonth = monthNumber(draftRows[0]?.month);
    const scheduleYear = Number(draftRows[0]?.year);
    if (!scheduleMonth || !Number.isInteger(scheduleYear)) {
      toast.error(
        "Month and Year must be completed in the Excel rows before importing.",
      );
      return;
    }
    const differentPeriod = draftRows.find(
      (row) =>
        monthNumber(row.month) !== scheduleMonth ||
        Number(row.year) !== scheduleYear,
    );
    if (differentPeriod) {
      toast.error(
        "All rows must have the same Month and Year. Different employee types and shifts can be imported together.",
      );
      return;
    }

    try {
      setImporting(true);
      const monthLabel = MONTH_NAMES[scheduleMonth - 1];
      const request = {
        // Legacy compatibility fields; the import procedure uses each employee's ShiftMasterId.
        shiftMasterId: 0,
        subDepartmentId: null,
        // These period values are derived from Excel, not from the viewing filters.
        scheduleMonth,
        scheduleYear,
        importSource: inputMode === "file" ? "FILE" : "PASTE",
        sourceFileName: inputMode === "file" ? selectedFileName || null : null,
        employees: draftRows.map((row) => ({
          personId: row.personId.trim(),
          // Do not trust a name supplied by Excel; the import stored procedure
          // resolves PersonName from authoritative employee records using PersonId.
          offDay: row.offDay.trim(),
          contractor: row.contractor || null,
          subDepartmentId:
            row.subDepartmentId && Number.isFinite(Number(row.subDepartmentId))
              ? Number(row.subDepartmentId)
              : null,
          subDepartment: row.subDepartment || null,
          subDepartmentCode: null,
          organization: row.organization || null,
          shift: row.shift || null,
          shiftKey: row.shiftKey || null,
          shiftMasterId: row.shiftMasterId,
          employeeType: row.employeeType,
          month: monthLabel,
          year: scheduleYear,
          scheduleMonth,
          scheduleYear,
        })),
      };

      const response = await ImportShiftRecords(request);
      const result = response?.data?.data ?? response?.data ?? response;
      const success = result?.success ?? result?.Success;
      const importedCount =
        result?.importedCount ??
        result?.ImportedCount ??
        request.employees.length;
      const message = result?.message ?? result?.Message;
      const rowErrors = result?.rowErrors ?? result?.RowErrors ?? [];

      if (!success) {
        const errorDetails = rowErrors
          .slice(0, 5)
          .map((item: any) => {
            const rowNumber = item?.sourceRowNumber ?? item?.SourceRowNumber;
            const validationMessage =
              item?.validationMessage ?? item?.ValidationMessage;
            return `Row ${rowNumber ?? "?"}: ${
              validationMessage ?? "Validation failed."
            }`;
          })
          .join("\n");
        toast.error(errorDetails || message || "Shift roster import failed.");
        return;
      }

      const importedRows: ImportedShiftRow[] = draftRows.map(
        ({ rowId: _rowId, ...row }) => ({
          ...row,
          department: row.department || loggedInDepartmentName,
          month: row.month || monthLabel,
          year: row.year || String(scheduleYear),
        }),
      );
      const importedShiftIds = new Set(
        importedRows.map((row) => String(row.shiftMasterId ?? "")),
      );
      setLocalImportedRows((previous) => [
        ...previous.filter((row) => {
          const sameImportedShift =
            importedShiftIds.has(String(row.shiftMasterId ?? "")) ||
            importedRows.some(
              (incoming) =>
                incoming.employeeType === row.employeeType &&
                normalize(incoming.shift) === normalize(row.shift),
            );
          const sameOrEarlierPeriod =
            Number(row.year) < scheduleYear ||
            (Number(row.year) === scheduleYear &&
              (monthNumber(row.month) ?? 0) <= scheduleMonth);
          return !(sameImportedShift && sameOrEarlierPeriod);
        }),
        ...importedRows,
      ]);
      setConfirmDialogOpen(false);
      setImportDialogOpen(false);
      confirmWindowStartRef.current = 0;
      setConfirmWindowStart(0);
      setConfirmationFilters({});
      setDraftRows([]);
      setPasteText("");
      setSelectedFileName("");
      toast.success(
        message || `${importedCount} employee records imported successfully.`,
      );
      setRosterRefreshKey((key) => key + 1);
      await loadShiftRows();
    } catch (error: any) {
      console.error("Shift import failed:", error);
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.Message ||
          error?.message ||
          "Unable to import shift data.",
      );
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      // Use the same schedule period selected in Roster Filters. The previous
      // implementation used the computer's current month/year, which could be
      // different from the roster currently displayed (for example, October
      // selected while the current month is November).
      const scheduleMonth = Number(selectedMonth);
      const scheduleYear = Number(selectedYear);
      if (
        !Number.isInteger(scheduleMonth) ||
        scheduleMonth < 1 ||
        scheduleMonth > 12 ||
        !Number.isInteger(scheduleYear) ||
        scheduleYear < 2000
      ) {
        toast.error(
          "Select a valid schedule month and year before downloading the sample.",
        );
        return;
      }
      const monthLabel = MONTH_NAMES[scheduleMonth - 1];

      // Prefer the roster already loaded for this exact period. This avoids a
      // second request returning a different/empty result after the table has
      // successfully loaded. If the page roster is not ready, request the same
      // selected period directly from ASAI ShiftImportRecords.
      const loadedEmployeeSource = serverRows.filter(
        (row) =>
          monthNumber(row.month) === scheduleMonth &&
          Number(row.year) === scheduleYear,
      );
      let employeeSource: any[] = loadedEmployeeSource;
      if (!employeeSource.length) {
        const rosterResponse = await GetCurrentShiftImportRecords({
          scheduleMonth,
          scheduleYear,
          shiftHours: 8,
        });
        employeeSource = extractRecords(rosterResponse);
      }

      const employeeMap = new Map<string, any>();
      employeeSource.forEach((item) => {
        const personId = getField(
          item,
          "personId",
          "PersonId",
          "Person_ID",
          "person_ID",
        );
        if (!personId) return;
        // Keep the first/current roster row for each employee.
        if (!employeeMap.has(normalize(personId)))
          employeeMap.set(normalize(personId), item);
      });
      const employees = Array.from(employeeMap.values());

      if (!employees.length) {
        toast.error(
          "No employee list was returned by the current ASAI 8-hour ShiftImportRecords roster for this schedule month and year.",
        );
        return;
      }

      const subDepartmentNames = Array.from(
        new Set(
          subDepartmentOptions
            .map((item) => stringValue(item.name))
            .filter(Boolean),
        ),
      );
      const shiftOptions = allSelectedShiftOptions;
      const shiftDropdownValues = Array.from(
        new Set(shiftOptions.map((item) => item.dropdownLabel)),
      );
      const typeDropdownValues = ["Payroll", "Contract", "Apprentice"];
      const offDayDropdownValues = [
        "MON",
        "TUE",
        "WED",
        "THU",
        "FRI",
        "SAT",
        "SUN",
      ];
      const monthDropdownValues = MONTH_NAMES;
      const yearDropdownValues = Array.from({ length: 101 }, (_, index) =>
        String(2000 + index),
      );

      if (!subDepartmentNames.length || !shiftDropdownValues.length) {
        toast.error(
          "Sub-department or shift dropdown options are missing. Refresh the page and try again.",
        );
        return;
      }

      const worksheetData: any[][] = [
        [
          "Person_ID",
          "Off_day",
          "Month",
          "Contractor",
          "Sub_Department",
          "Year",
          "Organization",
          "Employee_Type",
          "Shift",
        ],
      ];

      employees.forEach((item) => {
        const personId = getField(
          item,
          "personId",
          "PersonId",
          "Person_ID",
          "person_ID",
        );
        const offDay = getField(item, "offDay", "OffDay", "Off_day");
        const contractor = getField(item, "contractor", "Contractor");
        const subDepartment = getField(
          item,
          "subDepartmentName",
          "SubDepartmentName",
          "subDepartment",
          "SubDepartment",
          "sub_dept",
          "Sub_Dept",
        );
        const organization = getField(item, "organization", "Organization");
        const shiftMasterId = getField(item, "shiftMasterId", "ShiftMasterId");
        const shiftName = getField(
          item,
          "shiftName",
          "ShiftName",
          "shift",
          "Shift",
        );
        const rawShiftKey = getField(
          item,
          "shiftKey",
          "ShiftKey",
          "shiftNumber",
          "ShiftNumber",
        );
        const employeeTypeHints = [
          getField(item, "employeeType", "EmployeeType"),
          getField(item, "employeeCategory", "EmployeeCategory"),
          organization,
          getField(item, "shiftType", "ShiftType"),
        ];
        const currentEmployeeType =
          employeeTypeHints
            .map((value) => parseEmployeeType(value))
            .find(Boolean) ?? null;
        const parsedShiftToken = normalize(
          rawShiftKey || shiftName.match(/shift\s*([a-z0-9]+)/i)?.[1] || "",
        ).replace(/[^a-z0-9]/g, "");
        const expectedShiftKey =
          parsedShiftToken && !parsedShiftToken.startsWith("shift")
            ? `shift${parsedShiftToken}`
            : parsedShiftToken;
        const option = shiftOptions.find(
          (candidate) =>
            (shiftMasterId && String(candidate.id) === String(shiftMasterId)) ||
            (currentEmployeeType === candidate.employeeType &&
              (normalize(candidate.label) === normalize(shiftName) ||
                normalize(candidate.key).replace(/[^a-z0-9]/g, "") ===
                  expectedShiftKey)),
        );
        const sheetType = currentEmployeeType
          ? formatEmployeeTypeLabel(currentEmployeeType)
          : "";
        const sheetShift = option?.dropdownLabel ?? "";
        worksheetData.push([
          personId,
          offDay.toUpperCase(),
          monthLabel,
          contractor,
          subDepartment,
          scheduleYear,
          organization,
          sheetType,
          sheetShift,
        ]);
      });

      const workbook = new ExcelJS.Workbook();
      workbook.creator = "SSWhite Unified ERP";
      workbook.subject = "Shift Roster Import Template";
      workbook.title = "Shift Roster Import Template";
      workbook.company = "S.S. White Technologies India Pvt. Ltd.";

      const worksheet = workbook.addWorksheet("Shift Data");
      worksheet.addRows(worksheetData);
      [15, 12, 15, 23, 24, 10, 24, 24, 18, 42].forEach((width, index) => {
        worksheet.getColumn(index + 1).width = width;
      });
      worksheet.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
      worksheet.getRow(1).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFF4775B" },
      };
      worksheet.views = [{ state: "frozen", ySplit: 1 }];
      worksheet.autoFilter = { from: "A1", to: `J${worksheet.rowCount}` };

      const optionsSheet = workbook.addWorksheet("Options");
      typeDropdownValues.forEach((value, index) => {
        optionsSheet.getCell(index + 1, 1).value = value;
      });
      shiftDropdownValues.forEach((value, index) => {
        optionsSheet.getCell(index + 1, 2).value = value;
      });
      subDepartmentNames.forEach((value, index) => {
        optionsSheet.getCell(index + 1, 3).value = value;
      });
      offDayDropdownValues.forEach((value, index) => {
        optionsSheet.getCell(index + 1, 4).value = value;
      });
      monthDropdownValues.forEach((value, index) => {
        optionsSheet.getCell(index + 1, 5).value = value;
      });
      yearDropdownValues.forEach((value, index) => {
        optionsSheet.getCell(index + 1, 6).value = value;
      });
      optionsSheet.state = "hidden";
      workbook.definedNames.add(
        `'Options'!$A$1:$A$${typeDropdownValues.length}`,
        "EmployeeTypeList",
      );
      workbook.definedNames.add(
        `'Options'!$B$1:$B$${shiftDropdownValues.length}`,
        "ShiftList",
      );
      workbook.definedNames.add(
        `'Options'!$C$1:$C$${subDepartmentNames.length}`,
        "SubDepartmentList",
      );
      workbook.definedNames.add(
        `'Options'!$D$1:$D$${offDayDropdownValues.length}`,
        "OffDayList",
      );
      workbook.definedNames.add(
        `'Options'!$E$1:$E$${monthDropdownValues.length}`,
        "MonthList",
      );
      workbook.definedNames.add(
        `'Options'!$F$1:$F$${yearDropdownValues.length}`,
        "YearList",
      );

      const validationEndRow = Math.max(1000, worksheet.rowCount + 100);
      for (let row = 2; row <= validationEndRow; row += 1) {
        worksheet.getCell(`B${row}`).dataValidation = {
          type: "list",
          allowBlank: true,
          formulae: ["OffDayList"],
          showErrorMessage: true,
          errorStyle: "error",
          errorTitle: "Invalid off day",
          error: "Select MON, TUE, WED, THU, FRI, SAT or SUN.",
        };
        worksheet.getCell(`C${row}`).dataValidation = {
          type: "list",
          allowBlank: false,
          formulae: ["MonthList"],
          showErrorMessage: true,
          errorStyle: "error",
          errorTitle: "Invalid month",
          error: "Select a month from the dropdown list.",
        };
        worksheet.getCell(`E${row}`).dataValidation = {
          type: "list",
          allowBlank: false,
          formulae: ["SubDepartmentList"],
          showErrorMessage: true,
          errorStyle: "error",
          errorTitle: "Invalid sub-department",
          error: "Select a sub-department from the dropdown list.",
        };
        worksheet.getCell(`F${row}`).dataValidation = {
          type: "list",
          allowBlank: false,
          formulae: ["YearList"],
          showErrorMessage: true,
          errorStyle: "error",
          errorTitle: "Invalid year",
          error: "Select a year between 2000 and 2100.",
        };
        worksheet.getCell(`I${row}`).dataValidation = {
          type: "list",
          allowBlank: false,
          formulae: ["EmployeeTypeList"],
          showErrorMessage: true,
          errorStyle: "error",
          errorTitle: "Invalid employee type",
          error: "Select Payroll, Contract or Apprentice.",
        };
        worksheet.getCell(`J${row}`).dataValidation = {
          type: "list",
          allowBlank: false,
          formulae: ["ShiftList"],
          showErrorMessage: true,
          errorStyle: "error",
          errorTitle: "Invalid shift",
          error: "Select a configured shift from the dropdown list.",
        };
      }

      const instructions = workbook.addWorksheet("Instructions");
      instructions.addRows([
        ["Shift Roster Import Template"],
        [
          "The employee list is prefilled from the current roster. Review the employee details and update the row values as needed; do not add fake/example employees.",
        ],
        [
          "Employee_Type and Shift are selected per employee in columns I and J. One Excel file may contain multiple shifts and employee types.",
        ],
        [
          "Month and Year are initialized to the current period in this sample. Change them in Excel to the period you are importing. All rows in one import must share the same Month and Year.",
        ],
        [
          "The Employee Type, Shift, Month and Year controls on the web page are only for filtering/viewing the roster below. They are not used to decide the imported values.",
        ],
        [
          "Person_ID, Off_day, Month, Sub_Department, Year, Employee_Type and Shift are required. Person_Name is not needed; the backend resolves the employee's name from Person_ID.",
        ],
        [
          "The Shift dropdown contains the ShiftMaster shift name only. Select Employee_Type separately; the same shift number can exist under different employee types.",
        ],
        ["Column", "Description"],
        ["Person_ID", "Employee ID from the prefilled employee list"],
        ["Off_day", "Weekly off day; dropdown is available"],
        ["Month", "Schedule month; dropdown; required and taken from Excel"],
        ["Contractor", "Contractor name, where applicable"],
        [
          "Sub_Department",
          "Sub-department for the employee; select from dropdown",
        ],
        ["Year", "Schedule year; dropdown; required and taken from Excel"],
        [
          "Organization",
          "Optional display field; backend derives organization from Shift Master",
        ],
        [
          "Employee_Type",
          "Payroll, Contract or Apprentice; dropdown per employee",
        ],
        ["Shift", "Configured shift dropdown per employee"],
      ]);
      instructions.getColumn(1).width = 38;
      instructions.getColumn(2).width = 105;
      instructions.getRow(1).font = { bold: true, size: 14 };
      instructions.getRow(7).font = { bold: true };

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer as BlobPart], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const downloadLink = document.createElement("a");
      downloadLink.href = url;
      downloadLink.download = `Shift_Roster_${scheduleYear}_${String(
        scheduleMonth,
      ).padStart(2, "0")}.xlsx`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      downloadLink.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success(
        `Sample roster exported with ${
          worksheetData.length - 1
        } employee rows. Choose Type and Shift in Excel for each employee before importing.`,
      );
    } catch (error) {
      console.error("Failed to create the shift roster template:", error);
      toast.error("Unable to create the Excel template. Please try again.");
    }
  };

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = new Set<number>();
    for (let year = currentYear - 3; year <= currentYear + 2; year += 1)
      years.add(year);
    serverRows.forEach((row) => {
      const year = Number(row.year);
      if (Number.isInteger(year) && year > 1900) years.add(year);
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [serverRows]);

  const selectedPageTitle = `${
    MONTH_NAMES[Number(selectedMonth) - 1]
  } ${selectedYear} ${employeeTypeLabel} Shift Import`;

  const breadcrumbItems = [
    { to: "/", title: "ASAI" },
    { title: "8 Hours Shift" },
    { title: "Import Shift" },
    ...(loggedInDepartmentName ? [{ title: loggedInDepartmentName }] : []),
    ...(selectedShift ? [{ title: selectedShift.label }] : []),
  ];

  const closeImportDialog = () => {
    if (importing) return;
    setImportDialogOpen(false);
  };

  return (
    <PageContainer title={selectedPageTitle}>
      <Breadcrumb title={selectedPageTitle} items={breadcrumbItems} />

      <Box>
        {/* =================================================
            FILTERS — MATCH THE VIEW SHIFT PAGE LAYOUT
        ================================================= */}
        <Card sx={{ mb: 2, p: 0, overflow: "hidden" }}>
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            px={2}
            py={1.5}
            sx={{
              backgroundColor: "primary.light",
              color: "primary.main",
            }}
          >
            <Typography>Roster Filters</Typography>
          </Box>

          <Box sx={{ p: "10px 24px 16px" }}>
            {scopeError && (
              <Alert severity="warning" sx={{ mb: 1.5 }}>
                {scopeError}
              </Alert>
            )}

            {subDepartmentOptions.length === 0 && (
              <Alert severity="warning" sx={{ mb: 1.5 }}>
                {loggedInDepartmentId == null
                  ? "The logged-in user's department ID and allowed sub-departments have not been connected yet. Pass them from the existing auth state or wire fetchSubDepartmentsByDepartmentId."
                  : "No sub-departments were returned for the logged-in department. Check the department-scope API or auth state."}
              </Alert>
            )}

            <Box
              sx={{
                width: "100%",
                overflowX: "auto",
                // Outlined MUI labels sit slightly above the input border.
                // Reserve space inside the horizontal scroll viewport so its
                // vertical overflow does not clip the top of the labels.
                pt: 1.5,
                pb: 0.5,
                boxSizing: "border-box",
              }}
            >
              <Box
                aria-label="Shift import controls"
                sx={{
                  display: "grid",
                  gridTemplateColumns:
                    "minmax(130px, 0.9fr) minmax(190px, 1.45fr) minmax(115px, 0.85fr) minmax(95px, 0.65fr) auto auto auto",
                  gap: 1,
                  alignItems: "center",
                  minWidth: 1050,
                }}
              >
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Employee Type"
                  value={employeeType}
                  onChange={(event) => {
                    setEmployeeType(event.target.value as EmployeeType);
                    setSelectedShiftValue("");
                    setPage(0);
                    setSearchText("");
                    setColumnFilters({});
                  }}
                  disabled={importing || loadingScope}
                >
                  {EMPLOYEE_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Shift"
                  value={selectedShiftValue}
                  onChange={(event) => {
                    setSelectedShiftValue(event.target.value);
                    setPage(0);
                    setSearchText("");
                    setColumnFilters({});
                  }}
                  disabled={
                    importing ||
                    loadingScope ||
                    allSelectedShiftOptions.length === 0
                  }
                >
                  {selectedShiftOptions.map((shift) => (
                    <MenuItem key={shift.value} value={shift.value}>
                      {shift.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Month"
                  value={selectedMonth}
                  onChange={(event) => {
                    setSelectedMonth(event.target.value);
                    setPage(0);
                  }}
                  disabled={importing}
                >
                  {MONTH_NAMES.map((month, index) => (
                    <MenuItem key={month} value={String(index + 1)}>
                      {month}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Year"
                  value={selectedYear}
                  onChange={(event) => {
                    setSelectedYear(event.target.value);
                    setPage(0);
                  }}
                  disabled={importing}
                >
                  {yearOptions.map((year) => (
                    <MenuItem key={year} value={String(year)}>
                      {year}
                    </MenuItem>
                  ))}
                </TextField>

                <Button
                  variant="outlined"
                  size="small"
                  disabled={loadingRows || importing}
                  onClick={() => {
                    setRosterRefreshKey((key) => key + 1);
                    void loadShiftRows();
                  }}
                  startIcon={
                    loadingRows ? (
                      <CircularProgress size={15} />
                    ) : (
                      <RefreshOutlined />
                    )
                  }
                  sx={{
                    height: 40,
                    px: 1.75,
                    whiteSpace: "nowrap",
                    textTransform: "none",
                    alignSelf: { xs: "stretch", lg: "end" },
                  }}
                >
                  Refresh
                </Button>

                <Button
                  variant="contained"
                  size="small"
                  startIcon={
                    importing ? (
                      <CircularProgress size={15} color="inherit" />
                    ) : (
                      <CloudUploadOutlined />
                    )
                  }
                  disabled={
                    allSelectedShiftOptions.length === 0 ||
                    subDepartmentOptions.length === 0 ||
                    importing ||
                    loadingScope
                  }
                  onClick={openImportDialog}
                  sx={{
                    height: 40,
                    px: 1.5,
                    whiteSpace: "nowrap",
                    textTransform: "none",
                    boxShadow: "none",
                  }}
                >
                  {importing ? "Importing..." : "Import Excel"}
                </Button>

                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadOutlined />}
                  disabled={
                    allSelectedShiftOptions.length === 0 ||
                    subDepartmentOptions.length === 0 ||
                    importing ||
                    loadingScope
                  }
                  onClick={handleDownloadTemplate}
                  sx={{
                    height: 40,
                    px: 1.5,
                    whiteSpace: "nowrap",
                    textTransform: "none",
                  }}
                >
                  Download sample
                </Button>
              </Box>
            </Box>
          </Box>
        </Card>

        {/* Current roster from View Shift is shown directly below Import. */}
        <Box sx={{ mt: 2 }}>
          <MemoizedEightHoursViewShiftPage
            embedded
            refreshKey={rosterRefreshKey}
            selection={viewShiftSelection}
          />
        </Box>
      </Box>

      {/* Step 1: import source picker — upload/drop an Excel file or paste copied cells. */}
      <Dialog
        open={importDialogOpen}
        onClose={closeImportDialog}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            width: "96vw",
            maxWidth: 1100,
            maxHeight: "90vh",
            borderRadius: 3,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {importDialogOpen && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
              >
                <Box>
                  <Typography sx={{ fontSize: 21, fontWeight: 750 }}>
                    Import shift data
                  </Typography>
                  <Typography
                    sx={{ fontSize: 13, color: "text.secondary", mt: 0.35 }}
                  >
                    Employee type and shift are selected for each employee in
                    Excel. Schedule month/year also come from the Excel rows.
                  </Typography>
                </Box>
                <IconButton
                  onClick={closeImportDialog}
                  aria-label="Close import dialog"
                >
                  <CloseOutlined />
                </IconButton>
              </Stack>
            </DialogTitle>
            <Divider />
            <DialogContent
              sx={{
                px: { xs: 2, md: 3 },
                py: 2,
                overflowY: "auto",
              }}
            >
              <Tabs
                value={inputMode}
                onChange={(_, value: "file" | "paste") => setInputMode(value)}
                sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
              >
                <Tab
                  value="file"
                  label={
                    <Stack direction="row" spacing={1} alignItems="center">
                      Upload Excel
                    </Stack>
                  }
                  sx={{ textTransform: "none", fontWeight: 650 }}
                />

                <Tab
                  value="paste"
                  label={
                    <Stack direction="row" spacing={1} alignItems="center">
                      Paste from Excel
                    </Stack>
                  }
                  sx={{ textTransform: "none", fontWeight: 650 }}
                />
              </Tabs>

              {inputMode === "file" ? (
                <>
                  <input
                    ref={importDialogFileRef}
                    hidden
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileChange}
                  />

                  <Box
                    onDragEnter={(event) => {
                      event.preventDefault();
                      setDragging(true);
                    }}
                    onDragOver={(event) => {
                      event.preventDefault();
                      event.dataTransfer.dropEffect = "copy";
                    }}
                    onDragLeave={(event) => {
                      if (
                        !event.currentTarget.contains(
                          event.relatedTarget as Node | null,
                        )
                      ) {
                        setDragging(false);
                      }
                    }}
                    onDrop={(event) => void handleDrop(event)}
                    sx={{
                      border: "2px dashed",
                      borderColor: selectedFileName
                        ? "#9DEBCF"
                        : dragging
                        ? "primary.main"
                        : "#D9DDE2",
                      borderRadius: "12px",
                      background: selectedFileName
                        ? "linear-gradient(135deg, #F3FCF8 0%, #FFFFFF 100%)"
                        : dragging
                        ? "action.hover"
                        : "#FAFBFC",
                      minHeight: selectedFileName ? 294 : 230,
                      px: { xs: 2, md: 3 },
                      py: 3,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      position: "relative",
                      boxSizing: "border-box",
                      transition: "all 150ms ease",
                    }}
                  >
                    {!selectedFileName ? (
                      <>
                        <CloudUploadOutlined
                          sx={{
                            fontSize: 48,
                            color: "#77818E",
                            mb: 1,
                          }}
                        />

                        <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
                          {dragging
                            ? "Drop your file here"
                            : "Drag and drop your Excel file here"}
                        </Typography>

                        <Typography
                          sx={{
                            color: "#7B8492",
                            fontSize: 13,
                            mt: 0.75,
                            mb: 2,
                          }}
                        >
                          Supported formats: .xlsx, .xls and .csv
                        </Typography>

                        <Button
                          variant="outlined"
                          startIcon={<InsertDriveFileOutlined />}
                          onClick={() => importDialogFileRef.current?.click()}
                          sx={{
                            textTransform: "none",
                            fontWeight: 650,
                            borderRadius: "8px",
                            px: 2.5,
                            py: 1,
                            borderColor: "#FF8A70",
                            color: "#FF795D",
                            "&:hover": {
                              borderColor: "#FF795D",
                              backgroundColor: "#FFF5F1",
                            },
                          }}
                        >
                          Select file
                        </Button>
                      </>
                    ) : (
                      <>
                        <IconButton
                          size="medium"
                          aria-label="Remove selected file"
                          onClick={() => {
                            setSelectedFileName("");
                            setDraftRows([]);
                            setPasteText("");
                            setDragging(false);

                            if (importDialogFileRef.current) {
                              importDialogFileRef.current.value = "";
                            }
                          }}
                          sx={{
                            position: "absolute",
                            top: 16,
                            right: 16,
                            width: 44,
                            height: 44,
                            bgcolor: "#FFFFFF",
                            color: "#647080",
                            border: "1px solid #E5E9EE",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
                            "&:hover": {
                              bgcolor: "#FFF1EF",
                              color: "#E05243",
                            },
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 24 }} />
                        </IconButton>

                        <Box
                          sx={{
                            width: 154,
                            height: 154,
                            borderRadius: "50%",
                            background:
                              "radial-gradient(circle, #E1FAF0 0%, #E9FBF4 72%, rgba(233,251,244,0.2) 100%)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            position: "relative",
                            mb: 1.5,
                          }}
                        >
                          {/* Excel file illustration */}
                          <Box
                            sx={{
                              width: 68,
                              height: 88,
                              borderRadius: "7px 12px 7px 7px",
                              background:
                                "linear-gradient(145deg, #12B981, #079B69)",
                              position: "relative",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              boxShadow: "0 5px 12px rgba(5,150,105,0.16)",
                            }}
                          >
                            <Box
                              sx={{
                                position: "absolute",
                                top: 0,
                                right: 0,
                                width: 21,
                                height: 21,
                                bgcolor: "#A7F3D0",
                                clipPath: "polygon(0 0, 100% 100%, 0 100%)",
                              }}
                            />

                            <Typography
                              sx={{
                                fontSize: 42,
                                lineHeight: 1,
                                fontWeight: 800,
                                color: "#FFFFFF",
                                mt: 1,
                              }}
                            >
                              X
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              position: "absolute",
                              right: 2,
                              bottom: 8,
                              width: 58,
                              height: 58,
                              borderRadius: "50%",
                              bgcolor: "#08B981",
                              border: "4px solid #E8FBF4",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <CheckCircleIcon
                              sx={{
                                fontSize: 43,
                                color: "#FFFFFF",
                              }}
                            />
                          </Box>
                        </Box>

                        <Typography
                          sx={{
                            fontSize: 17,
                            fontWeight: 700,
                            color: "#171D2B",
                            textAlign: "center",
                            maxWidth: "100%",
                            overflowWrap: "anywhere",
                          }}
                        >
                          {selectedFileName}
                        </Typography>
                      </>
                    )}
                  </Box>
                </>
              ) : (
                <>
                  <Box
                    role="group"
                    aria-label="Excel roster paste grid"
                    tabIndex={0}
                    onClick={() => pasteCaptureRef.current?.focus()}
                    onKeyDown={(event) => {
                      if (
                        (event.ctrlKey || event.metaKey) &&
                        event.key.toLowerCase() === "v"
                      ) {
                        pasteCaptureRef.current?.focus();
                      }
                    }}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      overflow: "hidden",
                      backgroundColor: "background.paper",
                      outline: "none",
                      boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
                      "&:focus-visible": {
                        borderColor: "primary.main",
                        boxShadow: "0 0 0 3px rgba(244,119,91,0.16)",
                      },
                    }}
                  >
                    <textarea
                      ref={pasteCaptureRef}
                      aria-label="Paste Excel shift roster data"
                      tabIndex={-1}
                      value=""
                      onChange={(event) => {
                        // Normally Ctrl+V is intercepted by onPaste. This fallback
                        // supports browsers that route clipboard text via change.
                        const value = event.currentTarget.value;
                        if (value.trim()) {
                          setPasteText(value);
                          setSelectedFileName("");
                          setDraftRows([]);
                        }
                        event.currentTarget.value = "";
                      }}
                      onPaste={(event) => {
                        const clipboardText =
                          event.clipboardData.getData("text/plain");
                        if (!clipboardText.trim()) return;
                        event.preventDefault();
                        setPasteText(clipboardText);
                        setSelectedFileName("");
                        setDraftRows([]);
                      }}
                      spellCheck={false}
                      style={{
                        position: "fixed",
                        left: -10000,
                        top: 0,
                        width: 1,
                        height: 1,
                        opacity: 0,
                        pointerEvents: "none",
                      }}
                    />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 1,
                        px: 1.5,
                        py: 1,
                        backgroundColor: "#F7F9FC",
                        borderBottom: "1px solid #E3E8EF",
                        cursor: "text",
                      }}
                    >
                      <Typography
                        sx={{ fontSize: 13, fontWeight: 700, color: "#253044" }}
                      >
                        Excel paste grid
                      </Typography>
                      <Typography
                        sx={{ fontSize: 12, color: "text.secondary" }}
                      >
                        Click grid · Ctrl+V to paste or replace
                      </Typography>
                    </Box>
                    <Box sx={{ p: 1.25 }}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={1}
                        sx={{ mb: 1 }}
                      >
                        <Typography
                          sx={{
                            fontSize: 12,
                            fontWeight: 650,
                            color: "text.secondary",
                          }}
                        >
                          {pastePreviewRowCount > 0
                            ? "Pasted data preview"
                            : "Template preview"}
                        </Typography>
                        <Typography
                          sx={{ color: "text.secondary", fontSize: 12 }}
                        >
                          {pastePreviewRowCount === 0
                            ? "11 columns · ready for paste"
                            : `${pastePreviewRowCount} rows detected · all rows available (scroll to view)`}
                        </Typography>
                      </Stack>
                      <TableContainer
                        ref={pastePreviewContainerRef}
                        component={Paper}
                        variant="outlined"
                        onScroll={handlePastePreviewScroll}
                        sx={{
                          width: "100%",
                          height: 280,
                          overflow: "auto",
                          borderRadius: 1.5,
                          borderColor: "#DCE3EB",
                          cursor: "text",
                          "&::-webkit-scrollbar": { height: 10, width: 10 },
                          "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "#B8C2CF",
                            borderRadius: 8,
                          },
                        }}
                        onClick={() => pasteCaptureRef.current?.focus()}
                      >
                        <Table
                          stickyHeader
                          size="small"
                          sx={{
                            tableLayout: "fixed",
                            minWidth: 1620,
                            borderCollapse: "separate",
                            "& .MuiTableCell-root": {
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              borderRight: "1px solid #E6EBF1",
                              borderBottom: "1px solid #E6EBF1",
                              py: 0.85,
                              px: 1.25,
                              fontSize: 12,
                              height: 36,
                              boxSizing: "border-box",
                            },
                            "& .MuiTableHead-root .MuiTableCell-root": {
                              fontWeight: 700,
                              color: "#243044",
                              backgroundColor: "#F1F5F9",
                              zIndex: 2,
                              height: 42,
                            },
                            "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)":
                              {
                                backgroundColor: "#FAFCFE",
                              },
                            "& .MuiTableBody-root .MuiTableRow-root:hover": {
                              backgroundColor: "#FFF5F1",
                            },
                          }}
                        >
                          <TableHead>
                            <TableRow>
                              {pastePreviewHeaders.map(
                                (header, columnIndex) => (
                                  <TableCell
                                    key={`${normalizeHeader(
                                      header,
                                    )}-${columnIndex}`}
                                    sx={{
                                      width: getPastePreviewColumnWidth(header),
                                    }}
                                    title={stringValue(header)}
                                  >
                                    {stringValue(header) ||
                                      `Column ${columnIndex + 1}`}
                                  </TableCell>
                                ),
                              )}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {pastePreviewRows.length > 0 ? (
                              <>
                                {pastePreviewTopSpacerHeight > 0 && (
                                  <TableRow
                                    aria-hidden="true"
                                    sx={{ height: pastePreviewTopSpacerHeight }}
                                  >
                                    <TableCell
                                      colSpan={Math.max(
                                        1,
                                        pastePreviewHeaders.length,
                                      )}
                                      sx={{
                                        height: pastePreviewTopSpacerHeight,
                                        p: "0 !important",
                                        border: 0,
                                        lineHeight: 0,
                                      }}
                                    />
                                  </TableRow>
                                )}
                                {pastePreviewVisibleRows.map((row, index) => {
                                  const rowIndex =
                                    pastePreviewStartIndex + index;
                                  return (
                                    <TableRow
                                      key={`paste-preview-${rowIndex}`}
                                      hover
                                      sx={{ height: PASTE_PREVIEW_ROW_HEIGHT }}
                                    >
                                      {pastePreviewHeaders.map(
                                        (header, columnIndex) => {
                                          const cell = stringValue(
                                            row[columnIndex] ?? "",
                                          );
                                          return (
                                            <TableCell
                                              key={`${normalizeHeader(
                                                header,
                                              )}-${columnIndex}`}
                                              title={cell}
                                            >
                                              {cell || (
                                                <Box
                                                  component="span"
                                                  sx={{ color: "#C2CAD4" }}
                                                >
                                                  —
                                                </Box>
                                              )}
                                            </TableCell>
                                          );
                                        },
                                      )}
                                    </TableRow>
                                  );
                                })}
                                {pastePreviewBottomSpacerHeight > 0 && (
                                  <TableRow
                                    aria-hidden="true"
                                    sx={{
                                      height: pastePreviewBottomSpacerHeight,
                                    }}
                                  >
                                    <TableCell
                                      colSpan={Math.max(
                                        1,
                                        pastePreviewHeaders.length,
                                      )}
                                      sx={{
                                        height: pastePreviewBottomSpacerHeight,
                                        p: "0 !important",
                                        border: 0,
                                        lineHeight: 0,
                                      }}
                                    />
                                  </TableRow>
                                )}
                              </>
                            ) : (
                              <TableRow>
                                <TableCell
                                  colSpan={Math.max(
                                    1,
                                    pastePreviewHeaders.length,
                                  )}
                                  sx={{
                                    color: "text.secondary",
                                    height: 180,
                                    textAlign: "center",
                                  }}
                                >
                                  Click here, then press Ctrl+V to paste your
                                  Excel rows.
                                  <br />
                                  You can paste with or without the header row.
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 1,
                      mt: 1.25,
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setPasteText(PASTE_TEMPLATE_HEADER);
                        setSelectedFileName("");
                        setDraftRows([]);
                        pasteCaptureRef.current?.focus();
                      }}
                      sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                      Reset grid
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={preparePastedRows}
                      disabled={pastePreviewRowCount === 0}
                      sx={{ textTransform: "none", fontWeight: 650 }}
                    >
                      Prepare pasted data
                    </Button>
                  </Box>
                </>
              )}
            </DialogContent>
            <Divider />
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button
                variant="outlined"
                onClick={closeImportDialog}
                sx={{ textTransform: "none" }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={openConfirmation}
                disabled={draftRows.length === 0}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                Review &amp; confirm ({draftRows.length})
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Step 2: review all imported fields and correct row-level sub-department/shift before posting. */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => !importing && setConfirmDialogOpen(false)}
        fullWidth
        maxWidth="xl"
        PaperProps={{ sx: { borderRadius: 2, width: "96vw", maxWidth: 1800 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={2}
          >
            <Box>
              <Typography sx={{ fontSize: 21, fontWeight: 750 }}>
                Confirm imported employee data
              </Typography>
              <Typography
                sx={{ fontSize: 13, color: "text.secondary", mt: 0.35 }}
              >
                Verify each employee's type, shift, sub-department and schedule
                period. Different shifts and employee types can be imported
                together.
              </Typography>
            </Box>
            <IconButton
              onClick={() => !importing && setConfirmDialogOpen(false)}
              aria-label="Close confirmation dialog"
              disabled={importing}
            >
              <CloseOutlined />
            </IconButton>
          </Stack>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={1}
            sx={{ mb: 1.5 }}
          >
            <Alert severity="info" sx={{ flex: 1, py: 0.1 }}>
              {draftRows.length} employees ·{" "}
              {
                new Set(
                  draftRows.map(
                    (row) => `${row.employeeType}:${row.shiftMasterId}`,
                  ),
                ).size
              }{" "}
              shift/type combinations · {draftRows[0]?.month}{" "}
              {draftRows[0]?.year}
              {filteredDraftRows.length !== draftRows.length &&
                ` · ${filteredDraftRows.length} matching`}
            </Alert>
          </Stack>
          <TableContainer
            ref={confirmTableContainerRef}
            component={Paper}
            onScroll={handleConfirmationTableScroll}
            sx={{
              width: "100%",
              overflowX: "auto",
              overflowY: "auto",
              maxHeight: "62vh",
              contain: "layout paint",
            }}
          >
            <Table
              stickyHeader
              sx={{
                minWidth: 1750,
                tableLayout: "fixed",
                "& .MuiTableCell-root": {
                  whiteSpace: "nowrap",
                  verticalAlign: "middle",
                },
                "& .MuiTableHead-root .MuiTableCell-root": {
                  position: "sticky",
                  top: 0,
                  zIndex: 4,
                  backgroundColor: "#FAFBFB",
                },
                "& .MuiTableHead-root .confirmation-header-row .MuiTableCell-root":
                  {
                    top: 0,
                    zIndex: 7,
                    backgroundColor: "#FAFBFB",
                    height: CONFIRMATION_HEADER_HEIGHT,
                  },
                "& .MuiTableHead-root .confirmation-filter-row .MuiTableCell-root":
                  {
                    top: `${CONFIRMATION_HEADER_HEIGHT}px`,
                    zIndex: 6,
                    backgroundColor: "#F4F7F9",
                    height: CONFIRMATION_FILTER_HEIGHT,
                    boxSizing: "border-box",
                    borderBottom: "1px solid #DDE3E8",
                    padding: "5px 8px",
                  },
                // Fixed row heights must not override virtual spacer rows;
                // the spacer cells carry their own pixel height inline.
                "& .MuiTableBody-root .MuiTableCell-root:not(.confirmation-spacer-cell)":
                  {
                    padding: "4px 8px",
                    boxSizing: "border-box",
                    height: `${CONFIRMATION_ROW_HEIGHT}px`,
                  },
                "& .MuiTableBody-root .MuiTableRow-root:not(.confirmation-spacer-row)":
                  {
                    height: `${CONFIRMATION_ROW_HEIGHT}px`,
                  },
              }}
            >
              <TableHead>
                <TableRow className="confirmation-header-row">
                  {TABLE_COLUMNS.map((column) => (
                    <TableCell
                      key={column.key}
                      sx={{
                        width:
                          column.key === "personName"
                            ? 200
                            : column.key === "subDepartment"
                            ? 190
                            : column.key === "organization"
                            ? 180
                            : 145,
                      }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                  <TableCell sx={{ width: 230 }}>Shift</TableCell>
                  <TableCell sx={{ width: 110, textAlign: "center" }}>
                    Action
                  </TableCell>
                </TableRow>
                <ConfirmationFilterRow
                  appliedFilters={confirmationFilters}
                  onApply={applyConfirmationFilters}
                  disabled={importing}
                />
              </TableHead>
              <TableBody>
                {confirmationTopSpacerHeight > 0 && (
                  <TableRow
                    className="confirmation-spacer-row"
                    aria-hidden="true"
                    style={{ height: confirmationTopSpacerHeight }}
                    sx={{
                      height: `${confirmationTopSpacerHeight}px !important`,
                    }}
                  >
                    <TableCell
                      className="confirmation-spacer-cell"
                      colSpan={TABLE_COLUMNS.length + 2}
                      style={{ height: confirmationTopSpacerHeight }}
                      sx={{
                        height: `${confirmationTopSpacerHeight}px !important`,
                        minHeight: `${confirmationTopSpacerHeight}px !important`,
                        p: "0 !important",
                        border: 0,
                        lineHeight: 0,
                      }}
                    />
                  </TableRow>
                )}
                {draftRowsForConfirmation.map((row, index) => {
                  const absoluteIndex = confirmationStartIndex + index;
                  return (
                    <TableRow
                      key={row.rowId}
                      hover
                      sx={{ height: CONFIRMATION_ROW_HEIGHT }}
                    >
                      {TABLE_COLUMNS.map((column) => (
                        <TableCell key={column.key}>
                          {column.key === "subDepartment" ? (
                            <TextField
                              size="small"
                              fullWidth
                              value={row.subDepartment}
                              InputProps={{ readOnly: true }}
                              inputProps={{
                                "aria-label": `${column.label} row ${
                                  absoluteIndex + 1
                                }`,
                              }}
                            />
                          ) : column.key === "employeeType" ? (
                            <TextField
                              select
                              size="small"
                              fullWidth
                              value={row.employeeType}
                              onChange={(event) => {
                                const newType = event.target
                                  .value as EmployeeType;
                                setDraftRows((previous) =>
                                  previous.map((draft) => {
                                    if (draft.rowId !== row.rowId) return draft;
                                    const replacement =
                                      allSelectedShiftOptions.find(
                                        (option) =>
                                          option.employeeType === newType &&
                                          (option.key === draft.shiftKey ||
                                            normalize(option.label) ===
                                              normalize(draft.shift)),
                                      );
                                    return {
                                      ...draft,
                                      employeeType: newType,
                                      shift: replacement?.label ?? "",
                                      shiftKey: replacement?.key ?? "",
                                      shiftMasterId: replacement?.id
                                        ? Number(replacement.id)
                                        : undefined,
                                    };
                                  }),
                                );
                              }}
                            >
                              {EMPLOYEE_TYPES.map((type) => (
                                <MenuItem key={type} value={type}>
                                  {formatEmployeeTypeLabel(type)}
                                </MenuItem>
                              ))}
                            </TextField>
                          ) : (
                            <TextField
                              size="small"
                              fullWidth
                              value={String(row[column.key] ?? "")}
                              onChange={(event) =>
                                updateDraftCell(
                                  row.rowId,
                                  column.key as Exclude<
                                    EditableColumnKey,
                                    "employeeType" | "personName"
                                  >,
                                  event.target.value,
                                )
                              }
                              inputProps={{
                                "aria-label": `${column.label} row ${
                                  index + 1
                                }`,
                              }}
                            />
                          )}
                        </TableCell>
                      ))}
                      <TableCell>
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={
                            allSelectedShiftOptions.find(
                              (option) =>
                                option.id != null &&
                                Number(option.id) === Number(row.shiftMasterId),
                            )?.value ?? ""
                          }
                          onChange={(event) => {
                            const option = allSelectedShiftOptions.find(
                              (item) => item.value === event.target.value,
                            );
                            if (!option) return;
                            setDraftRows((previous) =>
                              previous.map((draft) =>
                                draft.rowId === row.rowId
                                  ? {
                                      ...draft,
                                      shift: option.label,
                                      shiftKey: option.key,
                                      shiftMasterId: Number(option.id),
                                      employeeType: option.employeeType,
                                    }
                                  : draft,
                              ),
                            );
                          }}
                          inputProps={{
                            "aria-label": `Shift row ${absoluteIndex + 1}`,
                          }}
                        >
                          {allSelectedShiftOptions
                            .filter(
                              (option) =>
                                option.employeeType === row.employeeType,
                            )
                            .map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                        </TextField>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          color="error"
                          variant="outlined"
                          size="small"
                          disabled={importing}
                          onClick={() =>
                            setDraftRows((previous) =>
                              previous.filter(
                                (draft) => draft.rowId !== row.rowId,
                              ),
                            )
                          }
                          sx={{ textTransform: "none", minWidth: 76 }}
                          aria-label={`Remove employee ${
                            row.personName || row.personId
                          }`}
                        >
                          Remove
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {confirmationBottomSpacerHeight > 0 && (
                  <TableRow
                    className="confirmation-spacer-row"
                    aria-hidden="true"
                    style={{ height: confirmationBottomSpacerHeight }}
                    sx={{
                      height: `${confirmationBottomSpacerHeight}px !important`,
                    }}
                  >
                    <TableCell
                      className="confirmation-spacer-cell"
                      colSpan={TABLE_COLUMNS.length + 2}
                      style={{ height: confirmationBottomSpacerHeight }}
                      sx={{
                        height: `${confirmationBottomSpacerHeight}px !important`,
                        minHeight: `${confirmationBottomSpacerHeight}px !important`,
                        p: "0 !important",
                        border: 0,
                        lineHeight: 0,
                      }}
                    />
                  </TableRow>
                )}
                {filteredDraftRows.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={TABLE_COLUMNS.length + 2}
                      align="center"
                      sx={{ py: 5, color: "text.secondary" }}
                    >
                      {draftRows.length === 0
                        ? "No rows to review."
                        : "No matching employees. Clear the column filters to see all rows."}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            disabled={importing}
            variant="outlined"
            onClick={() => {
              setConfirmDialogOpen(false);
              setImportDialogOpen(true);
            }}
            sx={{ textTransform: "none" }}
          >
            Back
          </Button>
          <Button
            variant="outlined"
            disabled={importing}
            onClick={() => setConfirmDialogOpen(false)}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={importing || draftRows.length === 0}
            startIcon={
              importing ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <CloudUploadOutlined />
              )
            }
            onClick={() => void handleConfirmImport()}
            sx={{ textTransform: "none", fontWeight: 750, minWidth: 165 }}
          >
            {importing
              ? "Importing..."
              : `Confirm import (${draftRows.length})`}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
}
